import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import {
  Mountain,
  Zap,
  Settings,
  Play,
  Pause,
  Clock,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Plus,
  Eye,
  ChevronRight,
  FileText,
  Mail,
  MessageSquare,
  Bell,
  Users,
  Calendar,
  DollarSign,
  Home,
  Sparkles,
  Activity,
  BarChart3
} from "lucide-react";
import AdminSidebar from "../components/layout/AdminSidebar";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";

const AutomationDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [owners, setOwners] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerTimeline, setOwnerTimeline] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, templatesRes, executionsRes, ownersRes] = await Promise.all([
        axios.get(`${API}/automation/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/automation/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/automation/executions?limit=20`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/owners`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setDashboard(dashboardRes.data);
      setTemplates(templatesRes.data.templates || []);
      setExecutions(executionsRes.data.executions || []);
      setOwners(ownersRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load automation data");
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerAutomation = async (templateId, ownerId = null) => {
    try {
      const response = await axios.post(
        `${API}/automation/trigger`,
        { template_id: templateId, owner_id: ownerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Automation triggered: ${response.data.execution_id}`);
      fetchData();
    } catch (error) {
      console.error("Error triggering automation:", error);
      toast.error("Failed to trigger automation");
    }
  };

  const handleOnboardOwner = async (ownerId) => {
    try {
      const response = await axios.post(
        `${API}/automation/onboard-owner`,
        { owner_id: ownerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Onboarding workflow started");
      fetchData();
    } catch (error) {
      console.error("Error onboarding owner:", error);
      toast.error("Failed to start onboarding");
    }
  };

  const handleViewTimeline = async (ownerId) => {
    try {
      const response = await axios.get(
        `${API}/automation/timeline/${ownerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOwnerTimeline(response.data);
      setSelectedOwner(owners.find(o => o.owner_id === ownerId));
    } catch (error) {
      console.error("Error fetching timeline:", error);
      toast.error("Failed to load timeline");
    }
  };

  const getLifecycleIcon = (stage) => {
    const icons = {
      new_owner: <Users className="w-4 h-4" />,
      active: <CheckCircle className="w-4 h-4" />,
      compliance_event: <AlertTriangle className="w-4 h-4" />,
      drc_project: <Home className="w-4 h-4" />,
      selling: <DollarSign className="w-4 h-4" />
    };
    return icons[stage] || <Activity className="w-4 h-4" />;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      running: { color: "bg-blue-100 text-blue-800", label: "Running" },
      completed: { color: "bg-green-100 text-green-800", label: "Completed" },
      failed: { color: "bg-red-100 text-red-800", label: "Failed" },
      pending: { color: "bg-yellow-100 text-yellow-800", label: "Pending" }
    };
    const config = statusConfig[status] || { color: "bg-gray-100 text-gray-800", label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getTimelineIcon = (type) => {
    const icons = {
      event: <Activity className="w-4 h-4" />,
      payment: <DollarSign className="w-4 h-4" />,
      notice: <AlertTriangle className="w-4 h-4" />,
      drc: <Home className="w-4 h-4" />,
      correspondence: <Mail className="w-4 h-4" />
    };
    return icons[type] || <FileText className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <Zap className="w-12 h-12 text-primary mx-auto animate-pulse" />
            <p className="mt-4 text-muted-foreground">Loading Automation Engine...</p>
          </div>
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
    <div className="min-h-screen bg-background" data-testid="automation-dashboard-page">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/board")}>
              <ChevronRight className="w-5 h-5 rotate-180" />
            </Button>
            <div>
              <h1 className="font-serif text-xl font-semibold flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Automation Engine
              </h1>
              <p className="text-xs text-muted-foreground">Owner Lifecycle & Workflow Automation</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Zap className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold">{dashboard.templates?.active || 0}</p>
                    <p className="text-xs text-muted-foreground">Active Templates</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Activity className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{dashboard.executions?.total_30_days || 0}</p>
                    <p className="text-xs text-muted-foreground">Executions (30 days)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{dashboard.executions?.success_rate?.toFixed(0) || 0}%</p>
                    <p className="text-xs text-muted-foreground">Success Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold">{dashboard.pending_tasks || 0}</p>
                    <p className="text-xs text-muted-foreground">Pending Tasks</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="dashboard" data-testid="automation-overview-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="templates-tab">
              <Settings className="w-4 h-4 mr-2" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="executions" data-testid="executions-tab">
              <Activity className="w-4 h-4 mr-2" />
              Executions
            </TabsTrigger>
            <TabsTrigger value="owners" data-testid="owner-lifecycle-tab">
              <Users className="w-4 h-4 mr-2" />
              Owner Lifecycle
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="dashboard">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Executions</CardTitle>
                  <CardDescription>Latest automation runs</CardDescription>
                </CardHeader>
                <CardContent>
                  {dashboard?.recent_executions?.length === 0 ? (
                    <p className="text-center py-4 text-muted-foreground">No recent executions</p>
                  ) : (
                    <div className="space-y-3">
                      {dashboard?.recent_executions?.slice(0, 5).map((exec) => (
                        <div key={exec.execution_id} className="flex items-center justify-between p-2 border rounded">
                          <div>
                            <p className="font-medium text-sm">{exec.template_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(exec.created_at).toLocaleString()}
                            </p>
                          </div>
                          {getStatusBadge(exec.status)}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                  <CardDescription>Common automation tasks</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => setActiveTab("templates")}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Manage Templates
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => setActiveTab("owners")}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Onboard New Owner
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => {
                        const duesTemplate = templates.find(t => t.template_id === "TPL-DUES-001");
                        if (duesTemplate) handleTriggerAutomation(duesTemplate.template_id);
                      }}
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Send Dues Reminders
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => setActiveTab("executions")}
                    >
                      <Activity className="w-4 h-4 mr-2" />
                      View All Executions
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card>
              <CardHeader>
                <CardTitle>Automation Templates</CardTitle>
                <CardDescription>Pre-built and custom workflow templates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {templates.map((template) => (
                    <div 
                      key={template.template_id} 
                      className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {getLifecycleIcon(template.lifecycle_stage)}
                            <span className="font-medium">{template.name}</span>
                            {template.is_default && <Badge variant="secondary">Default</Badge>}
                            {template.is_active ? (
                              <Badge className="bg-green-100 text-green-800">Active</Badge>
                            ) : (
                              <Badge variant="outline">Inactive</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{template.description}</p>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="text-xs">
                              {template.lifecycle_stage}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {template.trigger_type}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {template.actions?.length || 0} actions
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" onClick={() => setSelectedTemplate(template)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg">
                              <DialogHeader>
                                <DialogTitle>{template.name}</DialogTitle>
                                <DialogDescription>{template.description}</DialogDescription>
                              </DialogHeader>
                              <div className="mt-4 space-y-4">
                                <div>
                                  <h4 className="font-medium mb-2">Actions</h4>
                                  <div className="space-y-2">
                                    {template.actions?.map((action, i) => (
                                      <div key={i} className="flex items-center gap-2 p-2 bg-muted rounded text-sm">
                                        {action.type === "send_email" && <Mail className="w-4 h-4" />}
                                        {action.type === "send_sms" && <MessageSquare className="w-4 h-4" />}
                                        {action.type === "create_task" && <CheckCircle className="w-4 h-4" />}
                                        {action.type === "generate_document" && <FileText className="w-4 h-4" />}
                                        <span className="capitalize">{action.type?.replace(/_/g, " ")}</span>
                                        {action.template && <span className="text-muted-foreground">({action.template})</span>}
                                        {action.delay_hours && <span className="text-muted-foreground">+{action.delay_hours}h</span>}
                                        {action.delay_days && <span className="text-muted-foreground">+{action.delay_days}d</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                          <Button 
                            size="sm" 
                            disabled={!template.is_active}
                            onClick={() => handleTriggerAutomation(template.template_id)}
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Executions Tab */}
          <TabsContent value="executions">
            <Card>
              <CardHeader>
                <CardTitle>Execution History</CardTitle>
                <CardDescription>Recent automation runs and their status</CardDescription>
              </CardHeader>
              <CardContent>
                {executions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No executions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {executions.map((exec) => (
                      <div key={exec.execution_id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-sm">{exec.execution_id}</span>
                              {getStatusBadge(exec.status)}
                            </div>
                            <p className="font-medium">{exec.template_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(exec.created_at).toLocaleString()}
                              {exec.triggered_by && ` • By ${exec.triggered_by}`}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">
                              <span className="text-green-600">{exec.actions_completed?.length || 0}</span>
                              {" / "}
                              <span>{(exec.actions_completed?.length || 0) + (exec.actions_pending?.length || 0)}</span>
                              {" actions"}
                            </p>
                          </div>
                        </div>
                        {exec.error && (
                          <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                            {exec.error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Owner Lifecycle Tab */}
          <TabsContent value="owners">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Owner Management</CardTitle>
                  <CardDescription>Manage owner lifecycle and onboarding</CardDescription>
                </CardHeader>
                <CardContent>
                  {owners.length === 0 ? (
                    <p className="text-center py-4 text-muted-foreground">No owners found</p>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {owners.slice(0, 20).map((owner) => (
                        <div 
                          key={owner.owner_id} 
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                        >
                          <div>
                            <p className="font-medium">{owner.name}</p>
                            <p className="text-sm text-muted-foreground">{owner.email}</p>
                            <Badge variant="outline" className="mt-1 text-xs">
                              {owner.lifecycle_stage || "active"}
                            </Badge>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleViewTimeline(owner.owner_id)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => handleOnboardOwner(owner.owner_id)}
                            >
                              <Sparkles className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Owner Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    {selectedOwner ? `Timeline: ${selectedOwner.name}` : "Owner Timeline"}
                  </CardTitle>
                  <CardDescription>
                    {selectedOwner ? `${ownerTimeline?.total_events || 0} events` : "Select an owner to view timeline"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!ownerTimeline ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Select an owner to view their timeline</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {ownerTimeline.timeline?.map((event, i) => (
                        <div key={i} className="flex gap-3 p-2 border-l-2 border-primary/20 pl-4">
                          <div className="flex-shrink-0 mt-1">
                            {getTimelineIcon(event.type)}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{event.title}</p>
                            {event.description && (
                              <p className="text-xs text-muted-foreground">{event.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">{event.type}</Badge>
                              {event.status && <Badge variant="secondary" className="text-xs">{event.status}</Badge>}
                              <span className="text-xs text-muted-foreground">
                                {new Date(event.date).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
    </AdminSidebar>
  );
};

export default AutomationDashboardPage;
